import { useEffect, useState } from "react";
import { useApplyDiscountCodeMutation } from "../../../gql/__generated__/schema";
import { getTranslation } from "../../../shared/modules/config";

interface DiscountCodeProps {
  code: string;
  token: string;
  discountCodeSaved?: string | null;
  onSuccessApply: (discountCode: string) => Promise<void>;
  onSuccessRemove: () => Promise<void>;
}

export const useDiscountCode = ({
  code,
  token,
  discountCodeSaved,
  onSuccessApply,
  onSuccessRemove,
}: DiscountCodeProps) => {
  const [discountCode, setDiscountCode] = useState(discountCodeSaved);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [discountCodeApplyError, setDiscountCodeApplyError] = useState("");

  useEffect(() => {
    setDiscountCode(discountCodeSaved);
  }, [discountCodeSaved]);

  const [
    submitDiscountApi,
    { data: discountSubmitData, error: discountApiError },
  ] = useApplyDiscountCodeMutation();

  const applyDiscount = async (newDiscountCode: string) => {
    setDiscountCode(newDiscountCode);
    setDiscountLoading(true);
    setDiscountCodeApplyError("");

    try {
      if (!newDiscountCode) {
        throw new Error(
          getTranslation("shop_now_checkout_discount_code_not_provided"),
        );
      }
      await submitDiscountApi({
        variables: {
          cartToken: token,
          code,
          couponCodes: [newDiscountCode],
        },
      });
      await onSuccessApply(newDiscountCode);
    } catch (error: unknown) {
      if (error instanceof Error) {
        setDiscountCodeApplyError(error.message);
      } else console.error(error);
    } finally {
      setDiscountLoading(false);
    }
  };

  const removeDiscount = () => {
    setDiscountLoading(true);
    setDiscountCodeApplyError("");

    return submitDiscountApi({
      variables: {
        cartToken: token,
        code,
        couponCodes: [],
      },
    })
      .then(async () => {
        await onSuccessRemove();
        setDiscountCode(null);
      })
      .catch((error: Error) => {
        setDiscountCodeApplyError(error.message);
      })
      .finally(() => {
        setDiscountLoading(false);
      });
  };

  const discountError =
    discountApiError?.message ??
    discountSubmitData?.applyDiscountCode.errors[0]?.message;

  return {
    discountCode,
    applyDiscount,
    removeDiscount,
    submitDiscountApi,
    discountSubmitData,
    discountLoading,
    discountError,
    discountCodeApplyError,
  };
};
